import React from 'react';
import styled from 'styled-components';
import {CaptionContainer} from 'app/styles/heading';
import {
  LoadingIndicator,
  LOADING_MESSAGE,
  COLORS,
  ELEVATIONS,
  SIZES,
  Chips,
  Chip,
  ChipLabel,
  ChipIconContainer,
  Line,
  Placeholder,
  Typography,
  Button,
} from '@fupa/fupa-uikit';
import {FlexContainer} from 'app/styles/content';
import ProfileSeasonHeader from 'app/components/profile/ProfileSeasonHeader';
import ProfileMatchesFilterContainer from 'app/components/profile/ProfileMatchesFilterContainer';
import {object, string} from 'prop-types';
import {LinkOnComponent} from 'app/components/links/link';
import MatchesPieChart from 'app/components/profile/MatchesPieChart';
import GoalsBarChart from 'app/components/profile/GoalsBarChart';
import {
  ArrowDown,
  ArrowUp,
  Assist,
  Card,
  Football,
  TopEleven,
  Tracktics,
  TwoCards,
  PenaltyMissed,
} from 'app/components/icons';
import {updateStationFilter, updateStationShow} from 'app/routes/player/PlayerPageActions';
import {playerStationFilter} from 'app/routes/player/PlayerPageReducer';

const Container = styled(FlexContainer)`
  position: relative;
  flex-direction: column;
  flex: 0 100%;
`;

const LeagueBlock = styled.div`
  padding: ${SIZES[8]} 0;
  box-shadow: ${props => (props.show ? ELEVATIONS.ToggleBody : 'none')};
  ${props =>
    props.showsMatchRows &&
    `
    display: flex;
    flex-direction: column;
    justify-content: space-evenly;
    padding: ${SIZES[8]} ${SIZES[16]};
    gap: 0.5rem;
  `}
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StationDetails = styled.div`
  padding: ${SIZES['16']};
`;

const playerStatisticKeys = {
  matches: 'Spiele',
  goals: 'Tore',
  assists: 'Assists',
};

const coachStatisticKeys = {
  matches: 'Spiele',
  points: 'Punkte',
};

/**
 * Expansion Functionality is not used in this component, because the computation of the body-height would be too laborious.
 * Also, the animation would not be recognized in most cases, as the body-height is too long.
 */
const PlayerSeason = ({station, role, loadStation, matches, dispatch}) => {
  const profileSeasonId = station.id;
  const {items, isFetching, error, show, filter} = matches[profileSeasonId] ?? {
    items: null,
    isFetching: false,
    error: null,
    show: false,
    filter: playerStationFilter,
  };

  const setShow = (key, show) => dispatch(updateStationShow({key, show}));
  const setFilter = data => dispatch(updateStationFilter(data));

  const onClick = () => {
    if (!show && !items) {
      loadStation(profileSeasonId);
    } else {
      setShow(profileSeasonId, !show);
    }
  };

  let content = null;
  if (show) {
    const detail =
      role === 'coach' ? (
        <StationDetails>
          <MatchesPieChart
            wins={station.statistics.wins}
            defeats={station.statistics.defeats}
            draws={station.statistics.draws}
          />
          <GoalsBarChart ownGoals={station.statistics.ownGoals} againstGoals={station.statistics.againstGoals} />
        </StationDetails>
      ) : (
        createFilterChips(station, filter, setFilter, profileSeasonId)
      );

    const checkMatches = isFetching ? (
      <LoadingIndicator message={LOADING_MESSAGE.loadingMatches} bgColor />
    ) : !items.length && !isFetching && !error ? (
      <Placeholder show={show}>Noch keine Spiele eingetragen.</Placeholder>
    ) : error ? (
      <Placeholder show={show}>Beim Laden der Spiele ist ein Fehler aufgetreten.</Placeholder>
    ) : null;

    content = (
      <>
        <Wrapper>
          <LinkOnComponent to={`/team/${station.team.slug}`}>
            <Button text label='zum Teamkader' />
          </LinkOnComponent>
        </Wrapper>
        {detail}
        <LeagueBlock showsMatchRows={role === 'coach'} show={show}>
          {items?.length > 0
            ? items.map(matchGroup => {
                if (matchGroup?.matches?.length) {
                  return (
                    <ProfileMatchesFilterContainer
                      key={matchGroup.competition.competitionSeasonId}
                      matches={matchGroup.matches}
                      competition={matchGroup.competition}
                      station={station}
                      filter={filter}
                      type={role}
                    />
                  );
                }
              })
            : checkMatches}
        </LeagueBlock>
      </>
    );
  }

  return (
    <Container>
      <ProfileSeasonHeader
        showBoxShadow={show}
        onClick={onClick}
        station={station}
        role={role}
        statisticKeys={role === 'coach' ? coachStatisticKeys : playerStatisticKeys}
      />
      {content}
      {show ? null : <Line />}
    </Container>
  );
};

const createFilterChips = (station, filter, setFilter, key) => {
  const iconStyle = {fontSize: 16, margin: 'auto'};
  const FilterGenerator = statisticKey =>
    ({
      assists: {label: 'Assist', icon: <Assist style={{...iconStyle, color: COLORS.darkGrey}} />},
      topEleven: {label: 'Elf d. W.', icon: <TopEleven style={iconStyle} />},
      details: {label: 'Laufdaten', icon: <Tracktics style={iconStyle} />},
      goals: {label: 'Tore', icon: <Football style={{...iconStyle, color: COLORS.darkGrey}} />},
      penaltyMisses: {label: 'versch. 11m', icon: <PenaltyMissed style={{...iconStyle, color: COLORS.red}} />},
      penaltyHits: {label: 'verwan. 11m', icon: <Football style={{...iconStyle, color: COLORS.green}} />},
      redCard: {label: 'Rot', icon: <Card style={{...iconStyle, color: COLORS.red}} />},
      substituteIn: {label: 'Ein.', icon: <ArrowUp style={{...iconStyle, color: COLORS.greenIn}} />},
      substituteOut: {label: 'Aus.', icon: <ArrowDown style={{...iconStyle, color: COLORS.red}} />},
      yellowCard: {label: 'Gelb', icon: <Card style={{...iconStyle, color: COLORS.yellowCard}} />},
      yellowRedCard: {label: 'Gelb-Rot', icon: <TwoCards style={iconStyle} />},
    }[statisticKey]);

  const updateFilter = (key, stat) => {
    if (stat in filter) {
      setFilter({key, filter: {...filter, [stat]: !filter[stat]}});
    }
  };

  const statisticFilterChip = Object.keys(station.statistics).map(stat => {
    const filterOptions = FilterGenerator(stat);
    const filterAvailable = stat === 'details' ? station.statistics[stat].length > 0 : station.statistics[stat] > 0;
    if (filterOptions && filterAvailable) {
      const dynamicLabelValue = stat === 'details' ? '' : station.statistics[stat];
      return (
        <Chip key={`${key}-${stat}`} active={filter[stat]} onClick={() => updateFilter(key, stat)}>
          <ChipIconContainer>{filterOptions.icon}</ChipIconContainer>
          <ChipLabel>{dynamicLabelValue + ' ' + filterOptions.label}</ChipLabel>
        </Chip>
      );
    }
  });
  // checks if filter chips available or not
  return !!statisticFilterChip.filter(chip => chip !== undefined).length ? (
    <>
      <CaptionContainer>
        <Typography variant='caption1'>Filter</Typography>
      </CaptionContainer>
      <Chips>{statisticFilterChip}</Chips>
    </>
  ) : null;
};

PlayerSeason.propTypes = {
  station: object.isRequired,
  role: string.isRequired,
};

export {PlayerSeason};
